import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import CompliancePanel from "@/components/CompliancePanel";
import { SIGN_TYPES, MATERIALS, COLORS } from "@/lib/constants";
import { ArrowLeft, ArrowRight, Check, Upload, Image as ImageIcon, MapPin } from "lucide-react";

const STEPS = [
  "Informations commerciales", "Adresse", "Géolocalisation", "Type d'enseigne",
  "Caractéristiques", "Photos façade", "Documents", "Projet visuel", "Pré-contrôle", "Validation & dépôt",
];

const field = "w-full border border-input px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#000091]";
const lbl = "text-sm font-medium block mb-1";

export default function NewApplication() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [munis, setMunis] = useState([]);
  const [appId, setAppId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const [d, setD] = useState({
    municipality_id: "",
    commercial: { business_name: "", siret: "", contact_name: "", activity: "", phone: "", email: "" },
    address: { street: "", city: "", postal_code: "" },
    geo: { lat: "", lng: "" },
    sign_type: "",
    characteristics: { width_cm: "", height_cm: "", depth_cm: "", surface_m2: "", materials: [], colors: [], luminous: false, luminosity: "", text: "", fixation: "" },
    facade_photos: [],
    documents: [],
  });

  useEffect(() => { api.get("/municipalities").then((r) => setMunis(r.data)); }, []);

  const setC = (path, value) => {
    const [a, b] = path.split(".");
    if (b) setD((p) => ({ ...p, [a]: { ...p[a], [b]: value } }));
    else setD((p) => ({ ...p, [a]: value }));
  };
  const toggleArr = (key, val) => {
    const arr = d.characteristics[key];
    setC(`characteristics.${key}`, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const geoLocate = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation non supportée");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setC("geo.lat", pos.coords.latitude.toFixed(6)); setC("geo.lng", pos.coords.longitude.toFixed(6)); toast.success("Position récupérée"); },
      () => toast.error("Impossible d'obtenir la position (autorisez le GPS)")
    );
  };

  const onPhotos = (e) => {
    const files = Array.from(e.target.files).slice(0, 4);
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })))
      .then((imgs) => setC("facade_photos", [...d.facade_photos, ...imgs].slice(0, 4)));
  };

  const buildPayload = (status) => {
    const c = d.characteristics;
    const w = parseFloat(c.width_cm) || 0, h = parseFloat(c.height_cm) || 0;
    return {
      ...d,
      status,
      characteristics: {
        ...c,
        width_cm: parseFloat(c.width_cm) || 0, height_cm: parseFloat(c.height_cm) || 0,
        depth_cm: parseFloat(c.depth_cm) || 0,
        surface_m2: parseFloat(c.surface_m2) || (w && h ? +(w * h / 10000).toFixed(2) : 0),
        luminosity: parseFloat(c.luminosity) || 0,
      },
    };
  };

  const ensureSaved = async (status = "DRAFT") => {
    const payload = buildPayload(status);
    if (appId) { await api.put(`/applications/${appId}`, payload); return appId; }
    const { data } = await api.post("/applications", payload);
    setAppId(data.id);
    return data.id;
  };

  const runPrecheck = async () => {
    setSaving(true);
    try {
      const id = await ensureSaved();
      const { data } = await api.post(`/applications/${id}/precheck`);
      setPrecheck(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const next = async () => {
    if (step === 0 && !d.municipality_id) return toast.error("Sélectionnez une collectivité");
    if (step === 3 && !d.sign_type) return toast.error("Choisissez un type d'enseigne");
    if (step === 7) { await runPrecheck(); }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const id = await ensureSaved("SUBMITTED");
      await api.patch(`/applications/${id}/status`, { status: "SUBMITTED", note: "Dépôt par le commerçant" });
      toast.success("Dossier déposé auprès de la mairie !");
      nav(`/app/dossier/${id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/app")} className="text-sm text-slate-500 hover:text-[#000091] flex items-center gap-1 mb-4"><ArrowLeft size={16} /> Retour</button>
      <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-2">Nouvelle demande d'enseigne</h1>
      <p className="text-slate-500 mb-6">Étape {step + 1} sur {STEPS.length} — {STEPS[step]}</p>

      {/* progress */}
      <div className="flex gap-1 mb-8">
        {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 transition-colors ${i <= step ? "bg-[#000091]" : "bg-zinc-200"}`} />)}
      </div>

      <div className="card-flat p-6 lg:p-8 min-h-[320px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {step === 0 && (
              <div className="space-y-4">
                <div><label className={lbl}>Collectivité concernée</label>
                  <select data-testid="wiz-municipality" className={field} value={d.municipality_id} onChange={(e) => setC("municipality_id", e.target.value)}>
                    <option value="">Sélectionner...</option>
                    {munis.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">La mairie qui instruira votre demande.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={lbl}>Nom commercial</label><input data-testid="wiz-business" className={field} value={d.commercial.business_name} onChange={(e) => setC("commercial.business_name", e.target.value)} /></div>
                  <div><label className={lbl}>SIRET</label><input className={field} value={d.commercial.siret} onChange={(e) => setC("commercial.siret", e.target.value)} /></div>
                  <div><label className={lbl}>Contact</label><input className={field} value={d.commercial.contact_name} onChange={(e) => setC("commercial.contact_name", e.target.value)} /></div>
                  <div><label className={lbl}>Activité</label><input className={field} value={d.commercial.activity} onChange={(e) => setC("commercial.activity", e.target.value)} /></div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className={lbl}>Adresse de l'établissement</label><input data-testid="wiz-street" className={field} value={d.address.street} onChange={(e) => setC("address.street", e.target.value)} /></div>
                <div><label className={lbl}>Ville</label><input className={field} value={d.address.city} onChange={(e) => setC("address.city", e.target.value)} /></div>
                <div><label className={lbl}>Code postal</label><input className={field} value={d.address.postal_code} onChange={(e) => setC("address.postal_code", e.target.value)} /></div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-slate-600">Positionnez précisément votre établissement. Ces coordonnées serviront au contrôle terrain par l'agent.</p>
                <button onClick={geoLocate} data-testid="wiz-geolocate" className="bg-[#000091] text-white px-5 py-2.5 font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2"><MapPin size={18} /> Utiliser ma position GPS</button>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={lbl}>Latitude</label><input className={field} value={d.geo.lat} onChange={(e) => setC("geo.lat", e.target.value)} /></div>
                  <div><label className={lbl}>Longitude</label><input className={field} value={d.geo.lng} onChange={(e) => setC("geo.lng", e.target.value)} /></div>
                </div>
              </div>
            )}
            {step === 3 && (
              <div>
                <label className={lbl}>Type d'enseigne</label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {SIGN_TYPES.map((t) => (
                    <button key={t} data-testid={`wiz-signtype-${t}`} onClick={() => setC("sign_type", t)}
                      className={`border px-4 py-3 text-sm font-medium text-left transition-colors ${d.sign_type === t ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>{t}</button>
                  ))}
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-4 gap-4">
                  <div><label className={lbl}>Largeur (cm)</label><input data-testid="wiz-width" type="number" className={field} value={d.characteristics.width_cm} onChange={(e) => setC("characteristics.width_cm", e.target.value)} /></div>
                  <div><label className={lbl}>Hauteur (cm)</label><input data-testid="wiz-height" type="number" className={field} value={d.characteristics.height_cm} onChange={(e) => setC("characteristics.height_cm", e.target.value)} /></div>
                  <div><label className={lbl}>Profondeur (cm)</label><input type="number" className={field} value={d.characteristics.depth_cm} onChange={(e) => setC("characteristics.depth_cm", e.target.value)} /></div>
                  <div><label className={lbl}>Luminosité (lux)</label><input type="number" className={field} value={d.characteristics.luminosity} onChange={(e) => setC("characteristics.luminosity", e.target.value)} /></div>
                </div>
                <div>
                  <label className={lbl}>Matériaux</label>
                  <div className="flex flex-wrap gap-2">
                    {MATERIALS.map((m) => <button key={m} onClick={() => toggleArr("materials", m)} className={`border px-3 py-1.5 text-sm transition-colors ${d.characteristics.materials.includes(m) ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>{m}</button>)}
                  </div>
                </div>
                <div>
                  <label className={lbl}>Couleurs</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((c) => <button key={c} onClick={() => toggleArr("colors", c)} className={`border px-3 py-1.5 text-sm transition-colors ${d.characteristics.colors.includes(c) ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>{c}</button>)}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={lbl}>Texte de l'enseigne</label><input className={field} value={d.characteristics.text} onChange={(e) => setC("characteristics.text", e.target.value)} /></div>
                  <div><label className={lbl}>Type de fixation</label><input className={field} value={d.characteristics.fixation} onChange={(e) => setC("characteristics.fixation", e.target.value)} /></div>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={d.characteristics.luminous} onChange={(e) => setC("characteristics.luminous", e.target.checked)} /> Enseigne lumineuse</label>
              </div>
            )}
            {step === 5 && (
              <div>
                <label className="border-2 border-dashed border-border p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#000091] transition-colors">
                  <Upload size={32} className="text-slate-400 mb-2" />
                  <span className="text-sm font-medium">Ajouter des photos de la façade</span>
                  <span className="text-xs text-slate-500">JPG, PNG · max 4 photos</span>
                  <input data-testid="wiz-photos" type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                </label>
                {d.facade_photos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {d.facade_photos.map((p, i) => <img key={i} src={p} alt="façade" className="w-full h-24 object-cover border border-border" />)}
                  </div>
                )}
              </div>
            )}
            {step === 6 && (
              <div className="text-slate-600">
                <p className="mb-4">Documents complémentaires (plans, autorisation propriétaire, etc.). Optionnel pour le dépôt.</p>
                <div className="border border-dashed border-border p-6 text-center text-sm text-slate-500">Gestion documentaire simplifiée dans ce MVP — déposez vos pièces après validation.</div>
              </div>
            )}
            {step === 7 && (
              <div>
                <h3 className="font-display font-bold text-xl mb-3">Aperçu du projet</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="relative bg-zinc-100 border border-border aspect-video flex items-center justify-center overflow-hidden">
                    {d.facade_photos[0] ? <img src={d.facade_photos[0]} alt="façade" className="w-full h-full object-cover" /> : <ImageIcon size={40} className="text-slate-300" />}
                    <div className="absolute left-4 top-4 bg-[#000091] text-white px-3 py-1.5 text-sm font-bold shadow-lg" style={{ maxWidth: "60%" }}>
                      {d.characteristics.text || "VOTRE ENSEIGNE"}
                    </div>
                  </div>
                  <div className="text-sm space-y-1.5">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Récapitulatif</div>
                    <p><b>Type :</b> {d.sign_type}</p>
                    <p><b>Dimensions :</b> {d.characteristics.width_cm} × {d.characteristics.height_cm} cm</p>
                    <p><b>Matériaux :</b> {d.characteristics.materials.join(", ") || "—"}</p>
                    <p><b>Couleurs :</b> {d.characteristics.colors.join(", ") || "—"}</p>
                    <p><b>Lumineuse :</b> {d.characteristics.luminous ? "Oui" : "Non"}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-4">Génération de mockup simplifiée (le générateur avancé 3D/AR est prévu en version ultérieure).</p>
              </div>
            )}
            {step === 8 && (
              <div>
                <h3 className="font-display font-bold text-xl mb-4">Pré-contrôle automatique</h3>
                {saving && <p className="text-slate-500">Analyse en cours...</p>}
                {precheck ? <CompliancePanel result={precheck} /> : !saving && <button onClick={runPrecheck} className="bg-[#000091] text-white px-5 py-2.5 font-semibold">Lancer le pré-contrôle</button>}
              </div>
            )}
            {step === 9 && (
              <div>
                <h3 className="font-display font-bold text-xl mb-4">Validation & dépôt</h3>
                {precheck && (
                  <div className="mb-4 p-4 border border-border bg-zinc-50 text-sm">
                    Score de conformité : <b>{precheck.score}/100 — {precheck.level}</b>. Vous pouvez déposer votre dossier même en cas de points à vérifier ; la mairie tranchera.
                  </div>
                )}
                <p className="text-slate-600 mb-4">En déposant, votre dossier sera transmis à la mairie pour instruction. Vous serez notifié à chaque étape.</p>
                <button onClick={submit} data-testid="wiz-submit" disabled={saving} className="bg-[#000091] text-white px-6 py-3 font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2 disabled:opacity-60">
                  <Check size={18} /> {saving ? "Dépôt..." : "Déposer auprès de la mairie"}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="border border-border px-5 py-2.5 font-semibold disabled:opacity-40 hover:border-[#000091] transition-colors flex items-center gap-1"><ArrowLeft size={16} /> Précédent</button>
        {step < STEPS.length - 1 && (
          <button onClick={next} data-testid="wiz-next" className="bg-[#000091] text-white px-5 py-2.5 font-semibold hover:bg-blue-900 transition-colors flex items-center gap-1">Suivant <ArrowRight size={16} /></button>
        )}
      </div>
    </div>
  );
}
